/**
 * SettingsSubPage — web analogue of iOS SettingsSubPageLayout
 * (client-native/src/features/settings/components/SettingsSubPageLayout.tsx):
 * a pushed settings sub-screen with a back chevron on the left, a centered
 * title, and a card-wrapped body. Used by Settings' pushed navigation so each
 * sub-screen (Profile / Feed / Sound / Activities / Tutorials) reads as its own
 * screen — matching iOS, which `router.push`es a dedicated route per row —
 * instead of an inline accordion.
 *
 * The header mirrors the Settings hub header exactly (same BackButton + centered
 * bold title) so the push feels continuous.
 */
import { FC, ReactNode } from "react";
import { colors } from "@/ui";
import BackButton from "@/ui/BackButton";

interface SettingsSubPageProps {
  title: string;
  onBack: () => void;
  children: ReactNode;
}

const SettingsSubPage: FC<SettingsSubPageProps> = ({ title, onBack, children }) => (
  <div className="flex flex-col w-full gap-3 text-base">
    <div className="flex flex-row w-full items-center">
      <BackButton onPress={onBack} color={colors.primary} />
      <span
        className="text-lg font-bold flex-1 text-center"
        style={{ color: colors.primary }}
      >
        {title}
      </span>
      {/* spacer to keep the title visually centered against the back chevron */}
      <span style={{ width: 32 }} />
    </div>
    {/* Card body — iOS wraps each sub-screen's controls in a rounded Card. */}
    <div
      style={{
        backgroundColor: colors.white,
        borderRadius: 20,
        boxShadow: "var(--shadow-card-border)",
        padding: 16,
      }}
    >
      {children}
    </div>
  </div>
);

export default SettingsSubPage;
